<h2>Typography Sizes</h2>

<!-- Text Sizes Section -->
<section class="typography-section">
	<h3>Font Sizes & Line Heights</h3>

	@for (category of categories; track category) {
		<div class="typography-category">
			<h4>{{ category }}</h4>
			<table class="typography-table">
				<thead>
					<tr>
						<th>Preview</th>
						<th>Name</th>
						<th>Font Size Variable</th>
						<th>Line Height Variable</th>
						<th>Desktop</th>
						<th>Mobile (≤844px)</th>
					</tr>
				</thead>
				<tbody>
					@for (textSize of getTextSizesByCategory(category); track textSize.fontSizeVariable) {
						<tr>
							<td>
								<div
									class="text-preview"
									[style.font-size]="'var(' + textSize.fontSizeVariable + ')'"
									[style.line-height]="'var(' + textSize.lineHeightVariable + ')'"
								>
									Aa
								</div>
							</td>
							<td class="text-name">{{ textSize.name }}</td>
							<td>
								<div class="variable-cell">
									<div class="variable-codes">
										<code>{{ textSize.fontSizeVariable }}</code>
										<br />
										<code>{{ textSize.lineHeightVariable }}</code>
									</div>
									<button type="button"
										class="copy-button"
										(click)="copyToClipboard(textSize)"
										title="Copy both CSS properties"
									>
										<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
											<path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
										</svg>
									</button>
								</div>
							</td>
							<td>
								<div class="variable-usage">
									<code>font-size: var({{ textSize.fontSizeVariable }});</code>
									<code>line-height: var({{ textSize.lineHeightVariable }});</code>
								</div>
							</td>
							<td>
								<div class="size-values">
									<span class="font-size-value">{{ textSize.fontSizeValue }}</span>
									<span class="separator">/</span>
									<span class="line-height-value">{{ textSize.lineHeightValue }}</span>
								</div>
							</td>
							<td>
								@if (textSize.fontSizeValueMobile && textSize.lineHeightValueMobile) {
									<div class="size-values">
										<span class="font-size-value">{{ textSize.fontSizeValueMobile }}</span>
										<span class="separator">/</span>
										<span class="line-height-value">{{ textSize.lineHeightValueMobile }}</span>
									</div>
								} @else {
									<span class="no-change">—</span>
								}
							</td>
						</tr>
					}
				</tbody>
			</table>
		</div>
	}
</section>

<!-- CSS Variables Code Block -->
<section class="typography-section">
	<div class="code-block">
		<h4>CSS Variables (Desktop)</h4>
		<pre><code>:root &#123;
  /* Heading 1 */
  --h1-font-size: 56px;
  --h1-line-height: 64px;

  /* Heading 2 */
  --h2-font-size: 48px;
  --h2-line-height: 56px;

  /* Heading 3 */
  --h3-font-size: 40px;
  --h3-line-height: 48px;

  /* Heading 4 */
  --h4-font-size: 32px;
  --h4-line-height: 40px;

  /* Heading 5 */
  --h5-font-size: 24px;
  --h5-line-height: 32px;

  /* Body XL */
  --body-xl-font-size: 20px;
  --body-xl-line-height: 24px;

  /* Body L */
  --body-large-font-size: 18px;
  --body-large-line-height: 24px;

  /* Body M */
  --body-medium-font-size: 16px;
  --body-medium-line-height: 24px;

  /* Body S */
  --body-small-font-size: 14px;
  --body-small-line-height: 24px;

  /* Body XS */
  --body-xsmall-font-size: 12px;
  --body-xsmall-line-height: 16px;

  /* Caption */
  --caption-font-size: 10px;
  --caption-line-height: 16px;
&#125;</code></pre>
	</div>

	<div class="code-block">
		<h4>CSS Variables (Mobile - max-width: 844px)</h4>
		<pre><code>&#64;media (max-width: 844px) &#123;
  :root &#123;
    /* Heading 1 - Mobile usa Heading 2 Desktop */
    --h1-font-size: 48px;
    --h1-line-height: 56px;

    /* Heading 2 - Mobile usa Heading 3 Desktop */
    --h2-font-size: 40px;
    --h2-line-height: 48px;

    /* Heading 3 - Mobile usa Heading 4 Desktop */
    --h3-font-size: 32px;
    --h3-line-height: 40px;

    /* Heading 4 - Mobile usa Heading 5 Desktop */
    --h4-font-size: 24px;
    --h4-line-height: 32px;

    /* Heading 5 - Mobile usa Body XL Desktop */
    --h5-font-size: 20px;
    --h5-line-height: 24px;

    /* Body XL */
    --body-xl-font-size: 20px;
    --body-xl-line-height: 24px;

    /* Body L */
    --body-large-font-size: 18px;
    --body-large-line-height: 24px;

    /* Body M */
    --body-medium-font-size: 16px;
    --body-medium-line-height: 24px;

    /* Body S */
    --body-small-font-size: 14px;
    --body-small-line-height: 24px;

    /* Body XS */
    --body-xsmall-font-size: 12px;
    --body-xsmall-line-height: 16px;

    /* Caption */
    --caption-font-size: 10px;
    --caption-line-height: 16px;
  &#125;
&#125;</code></pre>
	</div>
</section>
